// src/components/Banner.tsx
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  TextField,
  InputAdornment,
  Badge,
} from "@mui/material";
import { Link } from "react-router-dom";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SearchIcon from "@mui/icons-material/Search";
import logo from "../img/logo.jpg";
import bannerImg from "../img/banner.webp";
import { useSelector } from "react-redux";
import { setSearchQuery } from "../store/searchSlice";
import { useDispatch } from "react-redux";
import { useMediaQuery, useTheme } from "@mui/material";

const Banner: React.FC = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [isScrolled, setIsScrolled] = useState(false);
  const cartItemsCount = useSelector((state: any) => state.cart.totalItems);
  const dispatch = useDispatch();

  const searchQuery = useSelector((state: any) => state.search.searchQuery);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(event.target.value));
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Use a buffer zone to prevent vibrating back and forth at 100px
      setIsScrolled((prevIsScrolled) => {
        if (!prevIsScrolled && currentScrollY > 120) {
          return true; // Collapse header after scrolling past 120px
        } else if (prevIsScrolled && currentScrollY < 60) {
          return false; // Expand header ONLY after scrolling back above 60px
        }
        return prevIsScrolled;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <AppBar
      position="sticky"
      sx={{
        width: "100%",
        transition: "height 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        willChange: "height, transform", // 👈 Signals the browser to hardware-accelerate rendering
        overflow: "hidden",
        //backgroundImage: "linear-gradient(90deg, #FAAAD2 0%, #86CBFC 100%)",
        bgcolor: '#0487a8',
        zIndex: 10,
        top: 0,
        backgroundSize: "cover",
        backgroundPosition: "center",
        boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
        height: { xs: "90px", sm: "110px", md: isScrolled ? "120px" : "160px" },
        transform: { xs: "translateY(0)", sm: "translateY(0)", md: isScrolled ? "translateY(-40px)" : "translateY(0)" }, // Adjusted scroll transition height to avoid harsh clipping
        margin: 0,
        padding: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          height: "100%",
          backgroundImage: `url(${bannerImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transition: "height 0.5s ease",
          opacity: 0.2,
          zIndex: 0,
        }}
      />

      {isMobileOrTablet && (
        <Box
          sx={{
            position: "absolute",
            top: "3px",
            left: 0,
            right: 0,
            textAlign: "center",
            flexGrow: 1,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: "white",
              fontWeight: "bold",
              fontSize: { xs: "18px", sm: "28px" },
              padding: "20px",
            }}
          >
            Welcome to Cozy Bud
          </Typography>
        </Box>
      )}

      <Toolbar

        disableGutters
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
          paddingX: 0,
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >


        {!isMobileOrTablet && (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <img
              src={logo}
              alt="Cozy Bud Logo"
              style={{
                height: isScrolled ? "70px" : "120px", // Scaled slightly smoother
                transition: "height 0.8s ease",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                borderRadius: "12px", // Slightly softer radius for cozy brand styling
                transform: isScrolled ? "translateY(18px)" : "translateY(0)",
              }}
            />
          </Box>
        )}
        {isMobileOrTablet && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={logo}
              alt="Cozy Bud Logo"
              style={{
                height: "60px",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                borderRadius: "8px",
              }}
            />
          </Box>
        )}

        {!isScrolled && (
          <Box
            sx={{
              textAlign: "center",
              flexGrow: 1,
              display: { xs: "none", md: "block" },
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: "bold",
                fontSize: { xs: "16px", sm: "20px", md: "32px" },
                letterSpacing: "0.5px"
              }}
            >
              Welcome to Cozy Bud
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: "white",
                fontSize: { xs: "12px", sm: "14px", md: "15px" },
                fontWeight: 300,
                opacity: 0.95,
                mb: 1
              }}
            >
              An online destination for adorable and high-quality baby and kids wear
            </Typography>
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              value={searchQuery}
              sx={{
                width: { xs: "150px", sm: "200px", md: "400px" },
                borderRadius: "20px",
                backgroundColor: "rgba(255, 255, 255, 0.8)", // Soft glassmorphic search background
                // 1. Clears native browser focus outlines
                "& input:focus": {
                  outline: "none",
                },

                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px", // Ensures focus matches rounded border shape

                  // 2. Removes border when hovering
                  "&:hover fieldset": {
                    borderColor: "transparent",
                  },

                  // 3. Removes default square border when clicked/focused
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent", // 👈 Prevents default sharp outline
                    boxShadow: "0 0 0 2px rgba(127, 190, 235, 0.4)", // Optional: soft pastel blue glow ring
                  },
                },

                // 4. Ensures default notch border stays hidden
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
              }}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#57b7fb" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {!isMobileOrTablet && isScrolled && (
          <Box
            sx={{
              textAlign: "center",
              flexGrow: 1,
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              paddingTop: "40px",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: "white",
                fontWeight: "bold",
                opacity: 1,
                fontSize: "24px",
                transition: "opacity 0.5s ease",
              }}
            >
              Welcome to Cozy Bud
            </Typography>
          </Box>
        )}

        {isMobileOrTablet && (
          <Box
            sx={{
              flexGrow: 1,
              pt: { xs: "40px", sm: "50px" },
              marginLeft: { xs: "10px", sm: "60px" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <TextField
              variant="outlined"
              placeholder="Search..."
              size="small"
              sx={{
                width: { xs: "150px", sm: "300px" },
                borderRadius: "20px",
                backgroundColor: "#DBD8F0",
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "transparent",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "transparent",
                    boxShadow: "0 0 0 2px rgba(127, 190, 235, 0.4)",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "transparent",
                },
                input: {
                  padding: "6px 8px",
                  fontSize: { xs: "11px", sm: "13px" },
                },
              }}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: { xs: "15px", sm: "18px" }, color: "#57b7fb" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            paddingTop: { xs: "40px", md: isScrolled ? "40px" : "80px" },
            transition: "padding 0.5s ease"
          }}
        >
          <Button color="success" component={Link} to="/" aria-label="Home">
            <HomeOutlinedIcon
              sx={{
                fontSize: {
                  xs: "24px",
                  sm: "30px",
                  md: isScrolled ? "30px" : "36px",
                },
              }}
            />
          </Button>
          <Button
            color="success"
            component={Link}
            to="/shoppingcart"
            aria-label="Shopping Cart"
          >
            <Badge
              badgeContent={cartItemsCount}
              color="error"
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ShoppingCartOutlinedIcon
                sx={{
                  fontSize: {
                    xs: "24px",
                    sm: "30px",
                    md: isScrolled ? "30px" : "36px",
                  },
                }}
              />
            </Badge>
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Banner;